/* eslint-disable react/jsx-handler-names */
import { useState, useCallback, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';

import { Mediator } from '@spotahome/soyuz-mediator';

import { OPEN_PHOTO_SLIDER } from '@spotahome/marketplace-common/src/channels';
import Backdrop from '@spotahome/marketplace-common/src/components/Backdrop';
import { PhotoListShape } from '@spotahome/marketplace-common/src/shapes/listing';

import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import FullScreenPhotoList from '../FullScreenPhotoList';

import {
  getBaseTracks,
  getExtraTracks,
  isTrackAllowedInFullScreen,
  getFloorPlanTrackIndex,
  getVideoTrackIndex,
  getImageTrackIndex,
  getMapTrackIndex
} from './utils/sliderAdapter';

import { IMG_TYPE } from './utils/constants';

import CarouselItemsWrapper from './CarouselItemsWrapper';
import FullScreenSlider from './FullScreenSlider';
import MainSliderItem from './MainSliderItem';
import MainSliderLabels from './MainSliderLabels';
import CloseSliderButton from './CloseSliderButton';

import './Slider.scss';

const HEADER_HEIGHT = 72;
const VIDEO_INDEX = 2;

const noop = () => {};

const getCarouselTracks = (fullScreenTracks, hasOtherRooms) =>
  [...fullScreenTracks, ...getExtraTracks(hasOtherRooms)].map(track => ({
    ...track,
    isHidden: track.type === IMG_TYPE
  }));

const getSliderTracks = ({
  photos,
  floorplanList,
  photoList,
  photo360List,
  videos,
  adTitle,
  hasOtherRooms,
  mapProps,
  videoWatermark
}) => {
  const fullScreenTracks = getBaseTracks({
    photos,
    floorplanList,
    photoList,
    photo360List,
    videos,
    adTitle,
    mapProps,
    videoWatermark
  });

  const carouselTracks = getCarouselTracks(fullScreenTracks, hasOtherRooms);

  return {
    tracks: carouselTracks,
    fullScreenTracks
  };
};

function offset(el) {
  const scrollLeft = window.scrollX;
  const scrollTop = window.scrollY;

  const rect = el.getBoundingClientRect();

  return {
    top: rect.top + scrollTop,
    left: rect.left + scrollLeft
  };
}

const navigateToTrackHash = ({ type }) => {
  const element = document.querySelector(`[data-slider=${type}]`);

  if (element && window) {
    const { top, left } = offset(element);

    window.scroll({
      top: top - HEADER_HEIGHT,
      left,
      behavior: 'smooth'
    });
  }
};

const Slider = ({
  handleCloseVideo = noop,
  showVideo = false,
  videos = [],
  adTitle = '',
  hasOtherRooms = false,
  map = {},
  viewer360Props = {},
  videoWatermark = null,
  listingId,
  deviceType,
  floorplanList,
  photoList,
  photo360List,
  onBackClick = noop
}) => {
  const [sliderOpen, setSliderOpen] = useState(null);
  const [trackId, setTrackId] = useState(null);
  const [initialItemIndex, setInitialItemIndex] = useState(0);

  const isListingSimplificationHeaderOriginal =
    useSoyuzExperiment('ListingSimplificationHeader').get('behaviour') ===
    'original';

  const sliderTracks = getSliderTracks({
    floorplanList,
    photoList,
    photo360List,
    videos,
    adTitle,
    hasOtherRooms,
    mapProps: map,
    viewer360Props,
    videoWatermark
  });

  const { fullScreenTracks, tracks } = sliderTracks;

  const mapTrackIndex = getMapTrackIndex(fullScreenTracks);
  const videoTrackIndex = getVideoTrackIndex(fullScreenTracks);
  const floorPlanTrackIndex = getFloorPlanTrackIndex(fullScreenTracks);

  const handleOpenSlider = (trackClickedId, itemIndex) => {
    if (itemIndex !== undefined && itemIndex !== null) {
      setInitialItemIndex(itemIndex);
    }
    setTrackId(trackClickedId);
    setSliderOpen(true);
  };

  useEffect(() => {
    const handleOpenFromMediator = (trackClickedId, trackItemClickedId) => {
      setInitialItemIndex(trackItemClickedId);
      handleOpenSlider(trackClickedId);
    };

    Mediator.subscribe(OPEN_PHOTO_SLIDER, handleOpenFromMediator);
    return () => {
      Mediator.unsubscribe(OPEN_PHOTO_SLIDER, handleOpenFromMediator);
    };
  }, []);

  const onHandleCloseSlider = useCallback(() => {
    handleCloseVideo();
    setSliderOpen(false);
  }, [handleCloseVideo]);

  useEffect(() => {
    if (showVideo) {
      handleOpenSlider(VIDEO_INDEX);
    }
  }, [showVideo]);

  const handleCarouselClick = (trackClickedId, track) => {
    if (isTrackAllowedInFullScreen(track)) {
      handleOpenSlider(trackClickedId);
    } else {
      navigateToTrackHash(track);
    }
  };

  const handlePhotoListImageClick = photoId => {
    const index = photoList.findIndex(p => p.id === photoId);
    handleOpenSlider(
      getImageTrackIndex(fullScreenTracks),
      index >= 0 ? index : 0
    );
  };

  const headerActions = [
    mapTrackIndex >= 0
      ? {
          label: trans('slider.carousel.view.on.map'),
          onClick: () => handleOpenSlider(mapTrackIndex),
          iconName: 'location_on_filled'
        }
      : null,
    videoTrackIndex >= 0
      ? {
          label: trans('slider.carousel.video'),
          onClick: () => handleOpenSlider(videoTrackIndex),
          iconName: 'smart_display'
        }
      : null,
    floorPlanTrackIndex >= 0
      ? {
          label: trans('slider.carousel.plan'),
          onClick: () => handleOpenSlider(floorPlanTrackIndex),
          iconName: 'space_dashboard'
        }
      : null
  ].filter(Boolean);

  return (
    <Fragment>
      <Backdrop
        isOpen={sliderOpen}
        handleClose={onHandleCloseSlider}
        closeOnClick
      >
        <CloseSliderButton onClick={onHandleCloseSlider} />
        <FullScreenSlider
          tracks={fullScreenTracks}
          defaultTrackToShow={trackId}
          handleClose={onHandleCloseSlider}
          currentTrackItem={initialItemIndex}
          listingId={listingId}
        />
      </Backdrop>
      <div className="slider__container">
        <MainSliderLabels onBackClick={onBackClick} listingId={listingId} />
        <MainSliderItem
          tracks={fullScreenTracks}
          onItemClick={handleOpenSlider}
          deviceType={deviceType}
        />
        {isListingSimplificationHeaderOriginal && (
          <div className="slider__carousel-wrapper">
            <CarouselItemsWrapper
              onClick={handleCarouselClick}
              trackData={tracks}
            />
          </div>
        )}
      </div>
      <FullScreenPhotoList
        photos={photoList}
        onImageClick={handlePhotoListImageClick}
        headerActions={headerActions}
        listingId={listingId}
      />
    </Fragment>
  );
};

Slider.propTypes = {
  handleCloseVideo: PropTypes.func,
  showVideo: PropTypes.bool,
  videos: PropTypes.arrayOf({
    title: PropTypes.string,
    video: PropTypes.string,
    thumbnail: PropTypes.string
  }),
  adTitle: PropTypes.string,
  hasOtherRooms: PropTypes.bool,
  map: PropTypes.shape(),
  viewer360Props: PropTypes.shape(),
  videoWatermark: PropTypes.node,
  listingId: PropTypes.string.isRequired,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired,
  floorplanList: PropTypes.arrayOf(PropTypes.string).isRequired,
  photoList: PhotoListShape.isRequired,
  photo360List: PhotoListShape.isRequired,
  onBackClick: PropTypes.func
};

export default Slider;
